import { StateCreator } from "zustand";
import { NavigateFunction } from "react-router";

import { RootState } from "store";
import {
  initialWizardCustomerState,
  initialWizardState,
  WizardCustomer
} from "models/wizard/WizardState";
import {
  createExternalUser,
  createInternalUser,
  setupCustomer,
  setUserService,
  toggleUserService
} from "helpers/WizardHelper";
import {
  addMultipleUsersPermissionOnDocumentFolder,
  addUserPermissionOnDocumentFolder,
  disableMultipleCustomerDocumentOptions,
  enableMultipleCustomerDocumentOptions,
  removeUserPermissionFromDocumentFolder,
  setupCustomerSelectedFolders
} from "views/documents/state/documentArchiveOptions";
import { ExternalUser, ExternalUserRole } from "models/customers/customers";
import { CustomersAPI } from "api/customers";
import { DocumentServicesAPI } from "api/documentServices";
import { UsersAPI } from "api/users";
import { userExistsOnCustomer } from "helpers/customerSettings/customerSettingsHelper";

export interface WizardSlice {
  showWarningFp: boolean;
  isSaving: boolean;
  isLoading: boolean;
  customer: WizardCustomer;
  hasErrors: boolean;

  resetWizard: () => void;
  cancelUpdateWarning: () => void;
  savingCustomer: () => void;
  updatedCustomer: () => void;
  failedToUpdateCustomer: () => void;
  savedCustomer: () => void;
  initUserServices: () => void;
  gettingCustomer: (isLoading: boolean) => void;
  getCustomer: (customer: WizardCustomer) => void;
  resetValidUserData: () => void;
  addCustomerUserToCustomer: (
    user: Partial<ExternalUser>,
    roles?: ExternalUserRole[],
    isNewUser?: boolean
  ) => void;
  removeCustomerUserFromCustomer: (id: string) => void;
  changeRoleForCustomerUser: (
    id: string,
    email: string,
    role: ExternalUserRole,
    value: boolean
  ) => void;
  toggleDocumentServices: (ids: string[], enabled: boolean) => void;
  toggleInviteAllExternalUsers: (invite: boolean) => void;
  toggleInviteExternalUser: (email: string, invite: boolean) => void;
  toggleUserDocumentService: (userId: string, serviceId: string) => void;
  toggleUserOnManyDocumentServices: (
    userId: string,
    serviceIds: string[],
    value: boolean
  ) => void;
  setFpService: (customerChoice: string) => void;
  setUserFpService: (customerChoice: string, userId: string) => void;
  fetchCustomer: (id: string) => void;
  saveCustomer: () => void;
  updateCustomer: (navigate: NavigateFunction) => void;
}

export const createWizardSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  WizardSlice
> = (set, get) => ({
  ...initialWizardState,

  resetWizard: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          customer: initialWizardCustomerState
        }
      }),
      false,
      "wizard/RESET_WIZARD"
    ),

  cancelUpdateWarning: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: false,
          showWarningFp: false
        }
      }),
      false,
      "wizard/CANCEL_UPDATE_WARNING"
    ),

  savingCustomer: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: false,
          showWarningFp: false,
          isSaving: true
        }
      }),
      false,
      "wizard/SAVING_CUSTOMER"
    ),

  updatedCustomer: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: false,
          isSaving: false
        }
      }),
      false,
      "wizard/UPDATED_CUSTOMER"
    ),

  failedToUpdateCustomer: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: true,
          isSaving: false
        }
      }),
      false,
      "wizard/FAILED_TO_UPDATE_CUSTOMER"
    ),

  savedCustomer: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: false,
          isSaving: false
        }
      }),
      false,
      "wizard/SAVED_CUSTOMER"
    ),

  initUserServices: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          customer: {
            ...wizard.customer,
            servicesLockedByNavigation: !wizard.customer
              .servicesLockedByNavigation
              ? true
              : wizard.customer.servicesLockedByNavigation
          }
        }
      }),
      false,
      "wizard/INIT_USER_SERVICES"
    ),

  gettingCustomer: (isLoading) =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          isLoading
        }
      }),
      false,
      "wizard/GETTING_CUSTOMER"
    ),

  getCustomer: (customer) =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          hasErrors: false,
          isLoading: false,
          customer: {
            ...setupCustomer(customer),
            documentServices: setupCustomerSelectedFolders(
              customer.documentServices || []
            )
          }
        }
      }),
      false,
      "wizard/GET_CUSTOMER"
    ),

  resetValidUserData: () =>
    set(
      ({ wizard }) => ({
        wizard: {
          ...wizard,
          customer: {
            ...wizard.customer,
            validUserData: true
          }
        }
      }),
      false,
      "wizard/RESET_VALID_USER_DATA"
    ),

  addCustomerUserToCustomer: (
    user: Partial<ExternalUser>,
    roles?: ExternalUserRole[],
    isNewUser?: boolean
  ) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const validUserData = !userExistsOnCustomer(
          customer.externalUsers,
          customer.internalUsers,
          user
        );

        // If the user already exists, just update validUserData and return
        if (!validUserData) {
          return {
            wizard: {
              ...state.wizard,
              customer: {
                ...customer,
                validUserData: false
              }
            }
          };
        }

        // User is new
        const sendInvite = isNewUser ? true : undefined;
        const sendNotification =
          !isNewUser && user.invitation == null ? true : undefined;

        const newUser: ExternalUser = {
          id: user.id ?? `ExternalUsers/${user.id}`,
          givenName: user.givenName ?? "",
          surName: user.surName ?? "",
          socialSecurityNumber: user.socialSecurityNumber ?? "",
          email: user.email ?? "",
          roles: roles && roles.length > 0 ? roles : [ExternalUserRole.User],
          invitation: user.invitation,
          sendInvite,
          isNewUser,
          sendNotification,
          verified: user.verified || false,
          verifiedAt: user.verifiedAt || new Date(),
          lastAccess: user.lastAccess || new Date()
        };

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              validUserData: true,
              externalUsers: [...customer.externalUsers, newUser]
            }
          }
        };
      },
      false,
      "wizard/ADD_CUSTOMER_USER_TO_CUSTOMER"
    );
  },

  removeCustomerUserFromCustomer: (id) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedExternalUsers = customer.externalUsers.filter(
          (user) => user.id !== id
        );
        // Sending ExternalUserRole.Admin for role in order to remove all permissions on all folders
        const updatedDocumentServices = removeUserPermissionFromDocumentFolder(
          customer.documentServices,
          id,
          ExternalUserRole.Admin
        );

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              externalUsers: updatedExternalUsers,
              documentServices: updatedDocumentServices
            }
          }
        };
      },
      false,
      "wizard/REMOVE_CUSTOMER_USER_FROM_CUSTOMER"
    );
  },

  changeRoleForCustomerUser: (id, email, role, value) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedExternalUsers = customer.externalUsers.map((user) => {
          if (user.email === email) {
            let updatedRoles = [...(user.roles || [])];

            if (value) {
              if (role === ExternalUserRole.Admin) {
                updatedRoles = [role];
              } else {
                // Add role if not present
                if (!updatedRoles.includes(role)) {
                  updatedRoles.push(role);
                }
              }
            } else {
              // Remove role
              updatedRoles = updatedRoles.filter((r) => r !== role);
            }

            // If no roles, default to empty array
            if (updatedRoles.length === 0) {
              updatedRoles = [];
            }
            // Remove "user" role when adding another role
            if (updatedRoles.length >= 2) {
              updatedRoles = [
                ...updatedRoles.filter((r) => r !== ExternalUserRole.User)
              ];
            }

            return {
              ...user,
              roles: updatedRoles
            };
          }
          return user;
        });

        const updatedDocumentServices = value
          ? addUserPermissionOnDocumentFolder(
              customer.documentServices,
              id,
              role
            )
          : removeUserPermissionFromDocumentFolder(
              customer.documentServices,
              id,
              role
            );

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              externalUsers: updatedExternalUsers,
              documentServices: updatedDocumentServices
            }
          }
        };
      },
      false,
      "wizard/CHANGE_ROLE_FOR_CUSTOMER_USER"
    );
  },

  toggleDocumentServices: (ids: string[], enabled: boolean) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        let updatedDocumentServices;

        if (enabled) {
          const internalUserIds = [...customer.internalUsers]
            .filter((user) => user.role?.toLowerCase() === "owner")
            .map((user) => user.id);

          const userIds = [...internalUserIds];

          updatedDocumentServices = enableMultipleCustomerDocumentOptions(
            customer.documentServices,
            ids,
            userIds
          );
        } else {
          updatedDocumentServices = disableMultipleCustomerDocumentOptions(
            customer.documentServices,
            ids
          );
        }
        const updatedPerRole = addMultipleUsersPermissionOnDocumentFolder(
          updatedDocumentServices,
          customer.externalUsers
        );

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              documentServices: updatedPerRole
            }
          }
        };
      },
      false,
      "wizard/TOGGLE_DOCUMENT_SERVICES"
    );
  },

  toggleInviteAllExternalUsers: (invite: boolean) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedExternalUsers = customer.externalUsers.map((user) => {
          return {
            ...user,
            sendInvite: invite
          };
        });

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              externalUsers: updatedExternalUsers
            }
          }
        };
      },
      false,
      "wizard/TOGGLE_INVITE_ALL_EXTERNAL_USERS"
    );
  },

  toggleInviteExternalUser: (email: string, invite: boolean) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedExternalUsers = customer.externalUsers.map((user) => {
          if (user.email === email) {
            return {
              ...user,
              sendInvite: invite
            };
          }
          return user;
        });

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              externalUsers: updatedExternalUsers
            }
          }
        };
      },
      false,
      "wizard/TOGGLE_INVITE_EXTERNAL_USER"
    );
  },

  toggleUserDocumentService: (userId, serviceId) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedDocumentServices = customer.documentServices.map((ds) => {
          if (ds.id === serviceId) {
            const updatedService = { ...ds };
            toggleUserService(updatedService, userId);
            return updatedService;
          }
          return ds;
        });

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              documentServices: updatedDocumentServices
            }
          }
        };
      },
      false,
      "wizard/TOGGLE_USER_DOCUMENT_SERVICE"
    );
  },

  toggleUserOnManyDocumentServices: (
    userId: string,
    serviceIds: string[],
    value: boolean
  ) => {
    set(
      (state) => {
        const customer = state.wizard.customer;

        const updatedDocumentServices = customer.documentServices.map((ds) => {
          if (serviceIds.includes(ds.id)) {
            const updatedService = { ...ds };
            setUserService(updatedService, userId, value);
            return updatedService;
          }
          return ds;
        });

        return {
          wizard: {
            ...state.wizard,
            customer: {
              ...customer,
              documentServices: updatedDocumentServices
            }
          }
        };
      },
      false,
      "wizard/TOGGLE_USER_ON_MANY_DOCUMENT_SERVICES"
    );
  },

  setFpService: (customerChoice) => {
    set(
      ({ wizard }) => {
        const customer = wizard.customer;

        return {
          wizard: {
            ...wizard,
            customer: {
              ...customer,
              fpService: {
                ...customer.fpService,
                customerChoice,
                userIds: []
              }
            }
          }
        };
      },
      false,
      "wizard/SET_FP_SERVICE"
    );
  },

  setUserFpService: (customerChoice, userId) => {
    set((state) => {
      const customer = state.wizard.customer;
      let updatedUserIds = [...(customer.fpService?.userIds ?? [])];

      const exists = updatedUserIds.includes(userId);

      if (exists) {
        updatedUserIds = updatedUserIds.filter((id) => id !== userId);
      } else {
        updatedUserIds.push(userId);
      }

      return {
        wizard: {
          ...state.wizard,
          customer: {
            ...customer,
            fpService: {
              ...customer.fpService,
              customerChoice,
              userIds: updatedUserIds
            }
          }
        }
      };
    });
  },

  fetchCustomer: async (id) => {
    const { wizard, error } = get();
    const { gettingCustomer, getCustomer } = wizard;

    try {
      gettingCustomer(true);
      const [customer, documentServices] = await Promise.all([
        CustomersAPI.getCustomerSettings(id),
        DocumentServicesAPI.getDocumentServices(id)
      ]);

      customer.documentServices = [...documentServices];
      getCustomer(customer);
    } catch (err) {
      error.appendErrorNotification(
        "Systemet kunde inte hämta information om kunden.",
        err
      );
    } finally {
      gettingCustomer(false);
    }
  },

  saveCustomer: async () => {
    const { wizard, user, global, error } = get();
    const { setWorkingCustomerId } = global;
    const { customer } = wizard;
    const userId = user.userInfo?.id;

    const customerPayload = {
      id: customer.id,
      isPrivate: customer.isPrivate,
      fpService: customer.fpService,
      documentServices: customer.documentServices,
      internalUsers: customer.internalUsers.map((internalUser) =>
        createInternalUser(internalUser)
      ),
      externalUsers: customer.externalUsers.map((externalUser) =>
        createExternalUser(externalUser)
      ),
      fromUser: userId
    };

    try {
      await CustomersAPI.saveCustomer(customerPayload);
      setWorkingCustomerId(customer.id);
      wizard.savedCustomer();
    } catch (err) {
      wizard.failedToUpdateCustomer();
      error.appendErrorNotification(
        "Sparande av inställningar misslyckades.",
        err
      );
    }
  },

  updateCustomer: async (navigate: NavigateFunction) => {
    const { wizard, error } = get();
    const { customer } = wizard;

    const documentServices = customer.documentServices;
    const id = customer.id;

    const externalUsers = customer.externalUsers.map((externalUser) =>
      createExternalUser(externalUser)
    );

    try {
      await Promise.all([
        DocumentServicesAPI.saveCustomerDocumentArchiveSettings(
          id,
          documentServices
        ),
        UsersAPI.saveCustomerExternalUserSettings(id, externalUsers)
      ]);
      wizard.updatedCustomer();
      navigate("/dashboard");
    } catch (err) {
      wizard.failedToUpdateCustomer();
      error.appendErrorNotification(
        "Ett fel uppstod när inställningarna skulle sparas. Kontrollera dina ändringar och försök igen.",
        err
      );
    }
  }
});